/**
 * Content Planner Service
 *
 * API client for the keyword research and clustering feature.
 */

import { apiClient } from './apiClient';
import { useTenantStore } from '../stores/useTenantStore';
import { getAuthHeaders } from '@/utils/authFetch';
import type {
  TopicalMapSummary,
  TopicalMapDetail,
  TopicalMapListResponse,
  ClusterSummary,
  ClusterDetail,
  CreateTopicalMapRequest,
  UpdateClusterRequest,
  BulkUpdateClusterStatusRequest,
  ClusterFilters,
  TopicalMapStatistics,
  CommonWord,
  TitleSuggestionResponse,
  KeywordCheckResponse,
  TopicSuggestionRequest,
  TopicSuggestionResponse,
  KeywordQualityInput,
  KeywordQualityResponse,
  SeedKeywordAnalysisResponse,
  CalendarResponse,
  KeywordFeedAhrefsRefreshRequest,
  KeywordFeedAhrefsRefreshResponse,
  KeywordFeedConfig,
  KeywordFeedGenerateRequest,
  KeywordFeedGenerateResponse,
  KeywordFeedItem,
  KeywordFeedListParams,
  KeywordFeedListResponse,
  KeywordFeedRefreshRequest,
  KeywordFeedRefreshResponse,
  KeywordFeedUpdateRequest,
  KeywordVettingJobDetail,
  KeywordVettingJobResponse,
} from '@/types/contentPlanner';

const API_BASE = '/content-planner';

// No fallback to the AgileCo test tenant: a missing client selection is an
// explicit error at request time, not a silent switch to test data.
const getTenantId = (): string => {
  const tenantId = useTenantStore.getState().currentTenant;
  if (!tenantId) throw new Error('No tenant selected');
  return tenantId;
};

// Prefer an explicitly-supplied tenant id (e.g. threaded from a react-query hook
// whose cache key is keyed on that same tenant) over resolving it at call time.
// This keeps the cache key and the request on the same tenant and avoids the
// old test-tenant fallback when a real tenant is known. See issue #186.
const resolveTenantId = (tenantId?: string) => tenantId || getTenantId();

function createKeywordVettingIdempotencyKey() {
  if (globalThis.crypto?.randomUUID) {
    return `keyword-vetting-${globalThis.crypto.randomUUID()}`;
  }
  return `keyword-vetting-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function getKeywordVettingErrorMessage(error: unknown): string {
  const candidate = error as {
    message?: string;
    response?: { data?: { detail?: unknown } };
  };
  const detail = candidate.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') {
    const structured = detail as { message?: unknown; warnings?: unknown[] };
    if (typeof structured.message === 'string') {
      const warningCount = Array.isArray(structured.warnings) ? structured.warnings.length : 0;
      return warningCount
        ? `${structured.message} (${warningCount} warning${warningCount === 1 ? '' : 's'})`
        : structured.message;
    }
  }
  return candidate.message || 'Keyword vetting could not be started.';
}

export const contentPlannerService = {
  // =============================================================================
  // Topical Maps
  // =============================================================================

  async createMap(data: CreateTopicalMapRequest): Promise<TopicalMapSummary> {
    const response = await apiClient.post<TopicalMapSummary>(
      `${API_BASE}/maps?tenant_id=${getTenantId()}`,
      data
    );
    return response.data;
  },

  createMapWithStreaming(
    data: CreateTopicalMapRequest,
    callbacks: {
      onCreated?: (mapId: string) => void;
      onProgress?: (status: string, progress: number, stage?: string) => void;
      onActivityLog?: (entries: Array<{ ts: string; level: string; step: string; msg: string; elapsed_ms: number }>, total: number, startedAt?: string) => void;
      onComplete?: (mapId: string, status: string, fromCache?: boolean) => void;
      onError?: (message: string) => void;
    }
  ): AbortController {
    const controller = new AbortController();
    const baseUrl = apiClient.defaults.baseURL || '';
    const url = `${baseUrl}${API_BASE}/maps/stream?tenant_id=${getTenantId()}`;

    getAuthHeaders().then(authHeaders => {
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream, application/json',
        ...authHeaders,
      },
      body: JSON.stringify(data),
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contentType = response.headers.get('content-type') || '';

        // Handle JSON response (cached result)
        if (contentType.includes('application/json')) {
          const jsonData = await response.json();
          callbacks.onComplete?.(jsonData.map_id, jsonData.status, jsonData.from_cache);
          return;
        }

        // Handle SSE streaming (new map creation)
        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No response body');
        }

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          const events = buffer.split('\n\n');
          buffer = events.pop() || '';

          for (const eventStr of events) {
            if (!eventStr.trim()) continue;

            const lines = eventStr.split('\n');
            let eventType = '';
            let eventData = '';

            for (const line of lines) {
              if (line.startsWith('event: ')) {
                eventType = line.slice(7);
              } else if (line.startsWith('data: ')) {
                eventData = line.slice(6);
              }
            }

            if (!eventType || !eventData) continue;

            try {
              const data = JSON.parse(eventData);

              switch (eventType) {
                case 'created':
                  callbacks.onCreated?.(data.map_id);
                  break;
                case 'progress':
                  callbacks.onProgress?.(data.status, data.progress, data.stage);
                  break;
                case 'complete':
                  callbacks.onComplete?.(data.map_id, data.status, data.from_cache);
                  break;
                case 'activity_log':
                  callbacks.onActivityLog?.(data.entries, data.total, data.started_at);
                  break;
                case 'error':
                  callbacks.onError?.(data.message);
                  break;
              }
            } catch {
              console.warn('Failed to parse SSE event data:', eventData);
            }
          }
        }

        // Release the connection after stream completes
        reader.cancel().catch(() => {});
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          callbacks.onError?.(error.message);
        }
      });
    }); // getAuthHeaders

    return controller;
  },

  async listMaps(params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<TopicalMapListResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('tenant_id', getTenantId());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());

    const response = await apiClient.get<TopicalMapListResponse>(
      `${API_BASE}/maps?${queryParams.toString()}`
    );
    return response.data;
  },

  async getMap(mapId: string): Promise<TopicalMapDetail> {
    const response = await apiClient.get<TopicalMapDetail>(
      `${API_BASE}/maps/${mapId}?tenant_id=${getTenantId()}`
    );
    return response.data;
  },

  async deleteMap(mapId: string): Promise<void> {
    await apiClient.delete(`${API_BASE}/maps/${mapId}?tenant_id=${getTenantId()}`);
  },

  async getMapStatistics(mapId: string): Promise<TopicalMapStatistics> {
    const response = await apiClient.get<TopicalMapStatistics>(
      `${API_BASE}/maps/${mapId}/statistics?tenant_id=${getTenantId()}`
    );
    return response.data;
  },

  // =============================================================================
  // Clusters
  // =============================================================================

  async listClusters(
    mapId: string,
    filters?: ClusterFilters & { limit?: number; offset?: number }
  ): Promise<ClusterSummary[]> {
    const params = new URLSearchParams();
    params.append('tenant_id', getTenantId());
    if (filters?.status) params.append('status', filters.status);
    if (filters?.intent) params.append('intent', filters.intent);
    if (filters?.min_volume) params.append('min_volume', filters.min_volume.toString());
    if (filters?.max_difficulty) params.append('max_difficulty', filters.max_difficulty.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());

    const response = await apiClient.get<ClusterSummary[]>(
      `${API_BASE}/maps/${mapId}/clusters?${params.toString()}`
    );
    return response.data;
  },

  async getCluster(mapId: string, clusterId: string): Promise<ClusterDetail> {
    const response = await apiClient.get<ClusterDetail>(
      `${API_BASE}/maps/${mapId}/clusters/${clusterId}?tenant_id=${getTenantId()}`
    );
    return response.data;
  },

  async updateCluster(
    mapId: string,
    clusterId: string,
    data: UpdateClusterRequest
  ): Promise<ClusterSummary> {
    const response = await apiClient.patch<ClusterSummary>(
      `${API_BASE}/maps/${mapId}/clusters/${clusterId}?tenant_id=${getTenantId()}`,
      data
    );
    return response.data;
  },

  async bulkUpdateClusterStatus(
    mapId: string,
    data: BulkUpdateClusterStatusRequest
  ): Promise<{ updated_count: number }> {
    const response = await apiClient.post<{ updated_count: number }>(
      `${API_BASE}/maps/${mapId}/clusters/bulk-status?tenant_id=${getTenantId()}`,
      data
    );
    return response.data;
  },

  // =============================================================================
  // Keyword Feed
  // =============================================================================

  async listKeywordFeed(
    params?: KeywordFeedListParams,
    tenantId?: string,
  ): Promise<KeywordFeedListResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('tenant_id', resolveTenantId(tenantId));
    if (params?.status) queryParams.append('status', params.status);
    if (typeof params?.include_skipped === 'boolean') {
      queryParams.append('include_skipped', String(params.include_skipped));
    }
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());

    const response = await apiClient.get<KeywordFeedListResponse>(
      `${API_BASE}/keyword-feed?${queryParams.toString()}`,
    );
    return response.data;
  },

  async refreshKeywordFeed(
    data: KeywordFeedRefreshRequest = {},
    tenantId?: string,
  ): Promise<KeywordFeedRefreshResponse> {
    const response = await apiClient.post<KeywordFeedRefreshResponse>(
      `${API_BASE}/keyword-feed/refresh?tenant_id=${resolveTenantId(tenantId)}`,
      data,
      { timeout: 90_000 },
    );
    return response.data;
  },

  async refreshKeywordFeedFromAhrefs(
    data: KeywordFeedAhrefsRefreshRequest,
    tenantId?: string,
  ): Promise<KeywordFeedAhrefsRefreshResponse> {
    const response = await apiClient.post<KeywordFeedAhrefsRefreshResponse>(
      `${API_BASE}/keyword-feed/ahrefs-refresh?tenant_id=${resolveTenantId(tenantId)}`,
      data,
      { timeout: 120_000 },
    );
    return response.data;
  },

  async updateKeywordFeedItem(
    itemId: string,
    data: KeywordFeedUpdateRequest,
    tenantId?: string,
  ): Promise<KeywordFeedItem> {
    const response = await apiClient.patch<KeywordFeedItem>(
      `${API_BASE}/keyword-feed/${itemId}?tenant_id=${resolveTenantId(tenantId)}`,
      data,
    );
    return response.data;
  },

  async generateKeywordFeedItem(
    itemId: string,
    data: KeywordFeedGenerateRequest = {},
    tenantId?: string,
  ): Promise<KeywordFeedGenerateResponse> {
    const response = await apiClient.post<KeywordFeedGenerateResponse>(
      `${API_BASE}/keyword-feed/${itemId}/generate?tenant_id=${resolveTenantId(tenantId)}`,
      {
        publish_immediately: false,
        export_to_gdocs: false,
        wordpress_publish_mode: 'none',
        ...data,
      },
    );
    return response.data;
  },

  async createKeywordVettingJob(
    tenantId: string,
    feedItemIds: string[],
  ): Promise<KeywordVettingJobResponse> {
    const response = await apiClient.post<KeywordVettingJobResponse>(
      '/hermes-keyword-vetting/jobs',
      {
        tenant_id: tenantId,
        feed_item_ids: feedItemIds,
        max_items: feedItemIds.length,
        dry_run: false,
      },
      {
        headers: { 'Idempotency-Key': createKeywordVettingIdempotencyKey() },
      },
    );
    return response.data;
  },

  async getKeywordVettingJob(
    jobId: string,
    tenantId: string,
  ): Promise<KeywordVettingJobDetail> {
    const response = await apiClient.get<KeywordVettingJobDetail>(
      `/hermes-keyword-vetting/jobs/${jobId}`,
      { params: { tenant_id: tenantId } },
    );
    return response.data;
  },

  async getKeywordFeedConfig(tenantId?: string): Promise<KeywordFeedConfig> {
    const response = await apiClient.get<KeywordFeedConfig>(
      `${API_BASE}/keyword-feed/config?tenant_id=${resolveTenantId(tenantId)}`,
    );
    return response.data;
  },

  async updateKeywordFeedConfig(data: KeywordFeedConfig, tenantId?: string): Promise<KeywordFeedConfig> {
    const response = await apiClient.put<KeywordFeedConfig>(
      `${API_BASE}/keyword-feed/config?tenant_id=${resolveTenantId(tenantId)}`,
      data,
    );
    return response.data;
  },

  // =============================================================================
  // Export
  // =============================================================================

  async exportMapCsv(mapId: string): Promise<Blob> {
    const response = await apiClient.get(
      `${API_BASE}/maps/${mapId}/export/csv?tenant_id=${getTenantId()}`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  // =============================================================================
  // Common Words
  // =============================================================================

  async getCommonWords(mapId: string): Promise<CommonWord[]> {
    try {
      const response = await apiClient.get<CommonWord[]>(
        `${API_BASE}/maps/${mapId}/common-words?tenant_id=${getTenantId()}`
      );
      return response.data;
    } catch {
      // Fallback if endpoint doesn't exist yet
      return [];
    }
  },

  // =============================================================================
  // Keyword Suggestions (LLM-based)
  // =============================================================================

  async getKeywordSuggestions(data: {
    services_offered?: string[];
    customer_pain_points?: string;
    marketing_focus?: string;
    ideal_client?: string;
    business_name?: string;
    exclude_existing?: boolean;
  }): Promise<{ suggestions: string[]; excluded_count: number }> {
    const response = await apiClient.post<{ suggestions: string[]; excluded_count: number }>(
      `${API_BASE}/keyword-suggestions?tenant_id=${getTenantId()}`,
      data,
      { timeout: 60_000 } // 60s — LLM generation
    );
    return response.data;
  },

  async analyzeSeedKeywords(data: {
    services_offered?: string[];
    customer_pain_points?: string;
    marketing_focus?: string;
    ideal_client?: string;
    business_name?: string;
  }): Promise<SeedKeywordAnalysisResponse> {
    const response = await apiClient.post<SeedKeywordAnalysisResponse>(
      `${API_BASE}/keyword-suggestions/analyze?tenant_id=${getTenantId()}`,
      data,
      { timeout: 90_000 } // 90s — LLM generation + sequential Ahrefs enrichment per keyword
    );
    return response.data;
  },

  // =============================================================================
  // Title Suggestions (SERP-powered)
  // =============================================================================

  /**
   * Get SEO-optimized title suggestions based on SERP analysis.
   *
   * @param keyword - Primary keyword for title generation
   * @returns Title suggestions with source information
   */
  async suggestTitles(keyword: string, selectedTopic?: string): Promise<TitleSuggestionResponse> {
    const body: Record<string, unknown> = { keyword };
    if (selectedTopic) {
      body.selected_topic = selectedTopic;
    }
    const response = await apiClient.post<TitleSuggestionResponse>(
      `/blog-creation/suggest-titles?tenant_id=${getTenantId()}`,
      body,
      { timeout: 60_000 } // 60s — LLM generation + SERP analysis
    );
    return response.data;
  },

  /**
   * Stream keyword suggestions using SSE for live updates.
   * Returns an EventSource-like interface for consuming the stream.
   */
  streamKeywordSuggestions(
    data: {
      services_offered?: string[];
      customer_pain_points?: string;
      marketing_focus?: string;
      ideal_client?: string;
      business_name?: string;
      exclude_existing?: boolean;
    },
    callbacks: {
      onStatus?: (message: string) => void;
      onChunk?: (content: string) => void;
      onKeywords?: (keywords: string[], count: number, excludedCount: number) => void;
      onError?: (message: string) => void;
      onComplete?: () => void;
    }
  ): AbortController {
    const controller = new AbortController();
    const baseUrl = apiClient.defaults.baseURL || '';
    const url = `${baseUrl}${API_BASE}/keyword-suggestions/stream?tenant_id=${getTenantId()}`;

    // Use fetch with POST for SSE (EventSource only supports GET)
    getAuthHeaders().then(authHeaders => {
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        ...authHeaders,
      },
      body: JSON.stringify(data),
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No response body');
        }

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Process complete SSE events
          const events = buffer.split('\n\n');
          buffer = events.pop() || ''; // Keep incomplete event in buffer

          for (const eventStr of events) {
            if (!eventStr.trim()) continue;

            const lines = eventStr.split('\n');
            let eventType = '';
            let eventData = '';

            for (const line of lines) {
              if (line.startsWith('event: ')) {
                eventType = line.slice(7);
              } else if (line.startsWith('data: ')) {
                eventData = line.slice(6);
              }
            }

            if (!eventType || !eventData) continue;

            try {
              const data = JSON.parse(eventData);

              switch (eventType) {
                case 'status':
                  callbacks.onStatus?.(data.message);
                  break;
                case 'chunk':
                  callbacks.onChunk?.(data.content);
                  break;
                case 'keywords':
                  callbacks.onKeywords?.(
                    data.keywords,
                    data.count,
                    data.excluded_count || 0
                  );
                  break;
                case 'error':
                  callbacks.onError?.(data.message);
                  break;
              }
            } catch {
              console.warn('Failed to parse SSE event data:', eventData);
            }
          }
        }

        // Release the connection after stream completes
        reader.cancel().catch(() => {});
        callbacks.onComplete?.();
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          callbacks.onError?.(error.message);
        }
      });
    }); // getAuthHeaders

    return controller;
  },

  // =============================================================================
  // Keyword Dedup Check
  // =============================================================================

  async checkKeywordDuplicate(keyword: string): Promise<KeywordCheckResponse> {
    const response = await apiClient.get<KeywordCheckResponse>(
      `/blog-creation/content/check-keyword`,
      { params: { keyword, tenant_id: getTenantId() } }
    );
    return response.data;
  },

  // =============================================================================
  // AI Topic Suggestions
  // =============================================================================

  async suggestTopics(data: TopicSuggestionRequest): Promise<TopicSuggestionResponse> {
    const response = await apiClient.post<TopicSuggestionResponse>(
      `/blog-creation/content/suggest-topics?tenant_id=${getTenantId()}`,
      data,
      { timeout: 60_000 } // 60s — LLM generation
    );
    return response.data;
  },

  // =============================================================================
  // Keyword Quality Assessment
  // =============================================================================

  async assessKeywordQuality(keywords: KeywordQualityInput[]): Promise<KeywordQualityResponse> {
    const response = await apiClient.post<KeywordQualityResponse>(
      `/blog-creation/content/keyword-quality?tenant_id=${getTenantId()}`,
      { keywords }
    );
    return response.data;
  },

  // =============================================================================
  // Cluster Browsing (Generate & Schedule)
  // =============================================================================

  async getClusters(): Promise<{ clusters: Array<{
    id: string;
    name: string;
    status: string;
    keyword_count: number;
    avg_volume: number;
    avg_difficulty: number;
  }> }> {
    const response = await apiClient.get(
      `${API_BASE}/clusters?tenant_id=${getTenantId()}`
    );
    return response.data;
  },

  async getClusterKeywords(clusterId: string): Promise<{ keywords: Array<{
    keyword: string;
    volume: number;
    difficulty: number;
    cpc?: number;
  }> }> {
    const response = await apiClient.get(
      `${API_BASE}/clusters/${clusterId}/keywords?tenant_id=${getTenantId()}`
    );
    return response.data;
  },

  // =============================================================================
  // Calendar
  // =============================================================================

  async getCalendarItems(params: {
    start_date?: string;
    end_date?: string;
    tenantId?: string;
    tenantIds?: string[];
    limit?: number;
    offset?: number;
    search?: string;
    status?: string;
  } = {}): Promise<CalendarResponse> {
    const tenantIds = params.tenantIds?.filter(Boolean) ?? [];
    const tenantId = params.tenantId || tenantIds[0] || getTenantId();
    const searchParams = new URLSearchParams();
    searchParams.set('tenant_id', tenantId || '');
    if (tenantIds.length > 1) {
      searchParams.set('tenant_ids', tenantIds.join(','));
    }
    if (params.start_date) searchParams.set('start_date', params.start_date);
    if (params.end_date) searchParams.set('end_date', params.end_date);
    if (params.limit) searchParams.set('limit', params.limit.toString());
    if (params.offset) searchParams.set('offset', params.offset.toString());
    if (params.search?.trim()) searchParams.set('search', params.search.trim());
    if (params.status && params.status !== 'all') searchParams.set('status', params.status);
    const response = await apiClient.get<CalendarResponse>(
      `${API_BASE}/calendar?${searchParams}`
    );
    return response.data;
  },

  async scheduleContent(contentId: string, scheduledDate: string | null): Promise<{ success: boolean; content_id: string; scheduled_date: string | null }> {
    const tenantId = getTenantId();
    const response = await apiClient.patch<{ success: boolean; content_id: string; scheduled_date: string | null }>(
      `${API_BASE}/schedule/${contentId}?tenant_id=${tenantId}`,
      { scheduled_date: scheduledDate }
    );
    return response.data;
  },
};
